import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { BusyIcon } from '../../components/loading';
import '../backlog/backlog.css';
// Shared BO detail-view anatomy/styles (sd-*) — same look as SettingsDetailPage.
import '../modules/SettingsDetailPage.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { ViewToggle } from '../../components/ViewToggle';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface SubCategoryRow {
  id:           number;
  categoryId:   number;
  categoryName: string;
  name:         string;
}
interface CategoryLookup { id: number; name: string; }

/** After-save behaviour for the Save trio (BO UI Standard §4). */
type AfterSave = 'stay' | 'new' | 'close';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function DwrcSubCategoriesPage() {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();
  const [rows,      setRows]      = useState<SubCategoryRow[]>([]);
  const [categories,setCategories]= useState<CategoryLookup[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [search,    setSearch]    = useState('');

  // Full-page Detail View (BO UI Standard §4 — list REPLACED, never a popup).
  // null = list; { id: null } = New; { id } = an existing record.
  // NOTE: this is a BESPOKE detail (not the shared SettingsDetailPage) because
  // the Parent Category select is fed from the in-memory /lookups payload —
  // there is no `{ data: Option[] }` endpoint the shared CrudField optionsUrl
  // could consume, and endpoint changes are out of scope.  Anatomy + classes
  // (sd-*) mirror SettingsDetailPage exactly: view-then-edit, Save trio,
  // prev/next, required tints.
  const [detail,    setDetail]    = useState<{ id: number | null } | null>(null);
  const [mode,      setMode]      = useState<'view' | 'edit'>('view');
  const [draft,     setDraft]     = useState<{ categoryId: number; name: string }>({ categoryId: 0, name: '' });
  const [triedSave, setTriedSave] = useState(false);
  // AppDataGrid owns sort / per-column filter / pagination; this page keeps only
  // the selection set (driven by AppDataGrid's checkbox column) for Edit/Delete.
  const [selected,  setSelected]  = useState<Set<string | number>>(new Set());
  const [loadMs,    setLoadMs]    = useState<number | null>(null);
  const [saving,    setSaving]    = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/regulatory-compliance/lookups');
      const data = (res.data as { data: { categories: CategoryLookup[]; subCategories: SubCategoryRow[] } }).data;
      setCategories(data.categories);
      setRows(data.subCategories);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load sub categories');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // ── Derived ────────────────────────────────────────────────────────────────
  // Only the global search box filters here; AppDataGrid does per-column filter,
  // sort and pagination.
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const t = search.toLowerCase();
    return rows.filter(x =>
      x.name.toLowerCase().includes(t) ||
      x.categoryName.toLowerCase().includes(t) ||
      String(x.id).includes(t)
    );
  }, [rows, search]);

  /** Ordered ids of the current list — powers the detail's prev/next arrows. */
  const siblingIds = useMemo(() => filtered.map(r => r.id), [filtered]);

  // Columns for the shared grid (parent Category pill + SubCategory name).
  const subCols: AppColumn<SubCategoryRow>[] = [
    { field: 'categoryName', header: 'Category', width: 240,
      render: r => (
        <span className="adg-pill" style={{ background: '#eef4fd', color: '#185FA5' }}>
          {r.categoryName}
        </span>
      ),
      text: r => r.categoryName,
    },
    { field: 'name', header: 'SubCategory', width: 320 },
  ];

  // ── Detail open/close ──────────────────────────────────────────────────────
  /** Open an EXISTING record — read-only VIEW first (ALPACA parity). */
  function openRecord(id: number, m: 'view' | 'edit' = 'view') {
    const row = rows.find(r => r.id === id);
    if (!row) return;
    setDraft({ categoryId: row.categoryId, name: row.name });
    setMode(m); setTriedSave(false); setDetail({ id });
  }
  /** New — straight to edit mode with an empty draft. */
  function openNew() {
    const firstCat = categories[0];
    setDraft({ categoryId: firstCat?.id ?? 0, name: '' });
    setMode('edit'); setTriedSave(false); setDetail({ id: null });
  }
  function closeDetail() { setDetail(null); setTriedSave(false); }

  function apiErr(e: unknown, fallback: string): string {
    const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
    return r?.data?.error?.message ?? (e instanceof Error ? e.message : fallback);
  }

  // ── Save (Save trio via the shared useSaveFlow — confirm → audited handler
  //   → "Saved successfully.") — same endpoints/payloads the old modal used. ──
  async function doSave(after: AfterSave) {
    setTriedSave(true);
    if (!draft.name.trim())  { notify.warning('SubCategory is required'); return; }
    if (!draft.categoryId)   { notify.warning('Parent Category is required'); return; }
    if (saving) return;
    const editing = detail?.id != null;
    let createdId: number | null = null;
    setSaving(true);
    try {
      await runSave({
        formName: 'Regulatory Compliance',   // matches the server-side audit FormName
        auditAction: editing ? 'edit' : 'create',
        save: async () => {
          const payload = { categoryId: draft.categoryId, name: draft.name.trim() };
          if (editing) {
            await api.put(`/api/v1/regulatory-compliance/subcategories/${detail!.id}`, payload);
          } else {
            const res = await api.post('/api/v1/regulatory-compliance/subcategories', payload);
            createdId = (res.data as { data?: { id?: number } }).data?.id ?? null;
          }
        },
        successMessage: editing ? 'Sub category updated successfully.' : 'Sub category created successfully.',
        onSaved: async () => {
          await load();
          if (after === 'close') { closeDetail(); return; }
          if (after === 'new')   { openNew(); return; }
          // 'stay' — remain on the saved record, back in read-only VIEW mode.
          if (!editing) {
            if (createdId != null) { setDetail({ id: createdId }); setMode('view'); }
            else closeDetail();   // couldn't resolve the new id → safest: return to list
          } else {
            setMode('view');
          }
        },
        onError: (e) => notify.error(`Save failed: ${apiErr(e, 'Could not save sub category.')}`),
      });
    } finally {
      setSaving(false);
    }
  }

  /** Edit the single selected row (toolbar button) — opens the full-page detail. */
  function openEditSelected() {
    if (selected.size === 0) { notify.warning('Select one row to edit.'); return; }
    if (selected.size > 1)   { notify.warning('Select only one row to edit.'); return; }
    openRecord(Number(Array.from(selected)[0]!), 'edit');
  }
  /** Bulk-delete every selected row (toolbar button). */
  async function deleteSelected() {
    if (selected.size === 0) { notify.warning('Select at least one row to delete.'); return; }
    const targets = rows.filter(r => selected.has(r.id));
    const names   = targets.map(t => `${t.categoryName} / ${t.name}`).join(', ');
    if (!(await confirm({
      title: targets.length === 1 ? 'Delete sub-category?' : 'Delete sub-categories?',
      message: `Delete ${targets.length} sub categor${targets.length === 1 ? 'y' : 'ies'}? (${names})`,
      tone: 'danger', confirmLabel: 'Delete',
    }))) return;
    if (saving) return;
    setSaving(true);
    try {
      await Promise.all(targets.map(t =>
        api.delete(`/api/v1/regulatory-compliance/subcategories/${t.id}`)));
      setSelected(new Set());
      await load();
      notify.success(`Deleted ${targets.length} sub categor${targets.length === 1 ? 'y' : 'ies'}.`);
    } catch (e: unknown) {
      notify.error(`Delete failed: ${apiErr(e, 'Could not delete one or more sub categories.')}`);
      await load();
    } finally {
      setSaving(false);
    }
  }

  const distinctParentsInGrid = useMemo(
    () => new Set(filtered.map(r => r.categoryName)).size,
    [filtered],
  );

  // ── Full-page Detail View (bespoke; shared sd-* anatomy — BO UI Standard §4) ─
  if (detail) {
    const viewOnly = mode === 'view';
    const recordName = detail.id != null
      ? (draft.name || 'Sub Category')
      : 'New Sub Category';
    const idx = detail.id != null ? siblingIds.indexOf(detail.id) : -1;
    const prevId = idx > 0 ? siblingIds[idx - 1] : null;
    const nextId = idx >= 0 && idx < siblingIds.length - 1 ? siblingIds[idx + 1] : null;
    const nameEmpty = triedSave && !draft.name.trim();
    const catEmpty  = triedSave && !draft.categoryId;

    // Glyphs = the EXACT BO UI Standard prototype .abtn icons (💾 🗎 💾 ↩︎).
    // VIEW mode (ALPACA parity): Edit (blue ✎) + Close — no Save/Cancel.
    const actionBar = (bottom?: boolean) => (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        {viewOnly ? (
          <>
            <button className="sd-abtn primary" onClick={() => setMode('edit')}>
              <span className="tb-i tb-i-blue">✎</span> Edit
            </button>
            <button className="sd-abtn cancel" onClick={closeDetail}>
              <span className="tb-i">↩︎</span> Close
            </button>
          </>
        ) : (
          <>
            <button className="sd-abtn primary" onClick={() => doSave('stay')} disabled={saving}>
              {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
            </button>
            <button className="sd-abtn" onClick={() => doSave('new')} disabled={saving}>
              <span className="tb-i">🗎</span> Save &amp; New
            </button>
            <button className="sd-abtn" onClick={() => doSave('close')} disabled={saving}>
              <span className="tb-i">💾</span> Save &amp; Close
            </button>
            <button className="sd-abtn cancel" onClick={closeDetail} disabled={saving}>
              <span className="tb-i">↩︎</span> Cancel
            </button>
          </>
        )}
      </div>
    );

    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* List/Detail segmented switch — Detail active; List returns to the grid. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') closeDetail(); }} />
        </div>
        <div className="bl-page mlv-host">
          <div className="bl-grid-area">
            <div className="sd-wrap">
              {/* Header — title + breadcrumb + prev/next record arrows */}
              <div className="sd-head">
                <div>
                  <h2>Sub Category – {recordName}</h2>
                  <div className="sd-bc">
                    <a onClick={closeDetail}>Home</a>
                    <i className="ti ti-chevron-right" />
                    <span>Reporting</span>
                    <i className="ti ti-chevron-right" />
                    <a onClick={closeDetail}>Sub Category</a>
                    <i className="ti ti-chevron-right" />
                    <span>{recordName}</span>
                  </div>
                </div>
                <div className="sd-navarrows">
                  <button className="sd-na" title="Previous record" disabled={!prevId || saving}
                    onClick={() => prevId != null && openRecord(prevId)}>‹</button>
                  <button className="sd-na next" title="Next record" disabled={!nextId || saving}
                    onClick={() => nextId != null && openRecord(nextId)}>›</button>
                </div>
              </div>

              {actionBar()}

              <div className="sd-body">
                <h3 className="sd-section-h">General Information</h3>
                <div className="sd-grid">
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="sd-categoryId">
                      Parent Category<span className="req">*</span>
                    </label>
                    <select
                      id="sd-categoryId"
                      className={`sd-input${catEmpty ? ' required-empty' : ''}${viewOnly ? ' readonly' : ''}`}
                      disabled={viewOnly}
                      value={draft.categoryId ? String(draft.categoryId) : ''}
                      onChange={e => setDraft(d => ({ ...d, categoryId: Number(e.target.value) }))}
                    >
                      <option value="">— select —</option>
                      {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="sd-name">
                      SubCategory<span className="req">*</span>
                    </label>
                    <input
                      id="sd-name"
                      className={`sd-input${nameEmpty ? ' required-empty' : ''}${viewOnly ? ' readonly' : ''}`}
                      type="text"
                      readOnly={viewOnly} disabled={viewOnly}
                      value={draft.name}
                      onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                      placeholder="e.g. EWPP Plant I & III"
                    />
                  </div>
                </div>
              </div>

              {actionBar(true)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .rc-sel-pill {
          display:inline-flex; align-items:center; gap:6px;
          background:color-mix(in srgb, var(--brand-accent, #e8670a) 10%, #fff); color:var(--brand-accent-hover, #c04a0a); border:1px solid #fbd6b8;
          padding:3px 4px 3px 10px; border-radius:12px; font-size:11px; font-weight:600;
        }
        .rc-sel-clear {
          background:transparent; border:none; color:var(--brand-accent-hover, #c04a0a); cursor:pointer;
          font-size:14px; line-height:1; padding:0 6px;
        }
        .rc-sel-clear:hover { color:#7a2a00; }
      `}</style>

      <div className="bl-page">
        <div className="bl-top">
          {/* List/Detail segmented switch (BO UI Standard) — top-LEFT.  Detail
              opens the selected row (or the first row) full-page. */}
          <div style={{ padding: '10px 0 0' }}>
            <ViewToggle view="list" detailDisabled={siblingIds.length === 0}
              onChange={(v) => {
                if (v !== 'detail') return;
                const id = selected.size === 1 ? Number(Array.from(selected)[0]!) : siblingIds[0];
                if (id != null) openRecord(id);
              }} />
          </div>
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Settings</span><i className="ti ti-chevron-right" />
              <span>Reporting</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Sub Category</span>
            </div>
            {/* Shared FormTitle — the ONE page heading (§18.6) ON the grey page,
                with the selection pill as a title-row chip. */}
            <div className="dp-title-row">
              <FormTitle pageId="dwrc-subcats" loadMs={loadMs} loading={loading} />
              {selected.size > 0 && (
                <span className="rc-sel-pill" style={{ marginLeft: 10 }}>
                  <i className="ti ti-circle-check" /> {selected.size} selected
                  <button className="rc-sel-clear" onClick={() => setSelected(new Set())} title="Clear selection">×</button>
                </span>
              )}
            </div>
            <div className="dp-subtitle">
              Sub categories grouped by parent category · Source: BTLIMS <code>tbl_DWRC_SubCategory</code>
            </div>
          </div>

          <div className="kpi-row">
            <div className="kpi">
              <div className="kpi-lbl">Total SubCategories</div>
              <div className="kpi-val kpi-blue">{loading ? '…' : rows.length}</div>
              <div className="kpi-chg">in BTLIMS lookup</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Parent Categories</div>
              <div className="kpi-val kpi-teal">{loading ? '…' : categories.length}</div>
              <div className="kpi-chg">all categories</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Filtered</div>
              <div className="kpi-val">{loading ? '…' : filtered.length}</div>
              <div className="kpi-chg">match current filter</div>
            </div>
            <div className="kpi">
              <div className="kpi-lbl">Parents in View</div>
              <div className="kpi-val">{loading ? '…' : distinctParentsInGrid}</div>
              <div className="kpi-chg">distinct parents shown</div>
            </div>
          </div>
        </div>

        <div className="bl-grid-area">
          {/* Toolbar on its OWN full-width row ON the grey page (BO UI Standard v2) —
              canonical order New · Edit · Delete · Refresh · Export to. */}
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              newLabel="Add New"
              onNew={openNew}
              newDisabled={categories.length === 0}
              onEdit={openEditSelected}
              editDisabled={selected.size !== 1}
              onDelete={deleteSelected}
              deleteDisabled={selected.size === 0}
              onRefresh={load}
              refreshing={loading}
              exportData={{ filename: 'dwrc-subcategories', ...gridExportMatrix(filtered, subCols) }}
            />
          </div>

          {/* Search — shared .list-search-row on the grey page, never inside the card. */}
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" placeholder="Search sub categories or parent…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
            </div>
            {(filtered.length !== rows.length) && (
              <span style={{ fontSize: 12, color: 'var(--ai-muted)' }}>{filtered.length} match{filtered.length !== 1 ? 'es' : ''}</span>
            )}
          </div>

          {/* ONE white grid card — grid + its single footer only. */}
          <div className="bl-card bl-card-flex">
            {error ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {error}
                <button onClick={load} style={{ marginLeft: 10, color: 'var(--brand-accent, #e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
              </div>
            ) : (
              /* Shared data grid — styling, per-column filters, sort, footer /
                 pagination, load-time and the selection checkboxes are all
                 standardised in <AppDataGrid>. */
              <AppDataGrid<SubCategoryRow>
                rows={filtered}
                columns={subCols}
                getRowId={r => r.id}
                loading={loading}
                loadMs={loadMs}
                selectable
                selectedIds={selected}
                onSelectionChange={setSelected}
                onRowClick={r => openRecord(r.id)}
                emptyText="No sub categories found"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
